import { Modal, Flex, Button } from "antd";
import { useState, useRef, useEffect } from "react";
import { EditFilled, EyeFilled } from "@ant-design/icons";
import { CustomTitle, CustomParagraph, CustomButton } from "../../components";
import emailAvatar from "../../assets/images/emailAvatar.svg";
import useAi from "../../assets/images/useAi.svg";
import { useToken } from "../../common/helper";
import AiEmailExchangeModal from "./AIEmailExchangeModal";
import { generateEmailParts } from "../../common/emailTemplates";

const EmailTranscriptModal = ({ open, onClose, onConfirm, shipment, alert }) => {
  const [isEditing, setIsEditing] = useState(false);
  const { token, userID, removeToken, decodedUserToken } = useToken();
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const editableRef = useRef();

  useEffect(() => {
    if (alert && shipment && !isEditing) {
      const { subject: newSubject, body: newBody } = generateEmailParts({
        alert,
        shipment,
        decodedToken: decodedUserToken(),
      });
      setSubject(newSubject);
      setMessage(newBody);
    }
  }, [alert, shipment, isEditing, decodedUserToken]);

  const handleToggleEdit = () => {
    if (isEditing && editableRef.current) {
      setMessage(editableRef.current.innerText);
    }
    setIsEditing((prev) => !prev);
  };

  return (
    <>
      <Modal open={open} onCancel={onClose} footer={null} centered width={600}>
        <Flex align="center" gap="8px" style={{ marginBottom: "24px" }}>
          <img src={useAi} alt="email icon" width={20} height={20} />
          <CustomTitle
            text="Email using AI Assistant"
            fontSize="20px"
            lineHeight="28px"
            fontWeight="700"
            marginBottom="0px"
          />
        </Flex>

        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: "12px" }}
        >
          <CustomParagraph
            text={isEditing ? "Edit message" : "Email preview"}
            fontSize="14px"
            color="black"
            fontWeight="600"
            marginBottom="0px"
          />
          <Button
            type="text"
            onClick={handleToggleEdit}
            icon={isEditing ? <EyeFilled /> : <EditFilled />}
            style={{
              fontWeight: 600,
              fontSize: "12px",
              background: "#F3F3F3",
              borderRadius: "28px",
              padding: "4px 10px",
              height: "32px",
            }}
          >
            {isEditing ? "Preview" : "Edit message"}
          </Button>
        </Flex>

        {/* Message box */}
        <div
          style={{
            border: !isEditing ? "2px solid #E2E2E2" : "none",
            borderRadius: "8px",
            padding: "16px",
            background: isEditing ? "#F3F3F3" : "#fff",
            marginBottom: "24px",
            position: "relative",
          }}
        >
          <Flex gap="12px" align="start">
            {!isEditing && (
              <img src={emailAvatar} alt="avatar" width={32} height={32} />
            )}
            <div style={{ fontSize: "12px", color: "#000", width: "100%" }}>
              {!isEditing && (
                <>
                  <p
                    style={{
                      fontWeight: 600,
                      fontSize: "12px",
                      marginBottom: "0px",
                    }}
                  >
                    {decodedUserToken()?.company_name}
                    <span style={{ fontWeight: 400 }}>
                      {`<${
                        decodedUserToken()?.email?.toLowerCase() ??
                        "support"
                      }`}
                    </span>
                  </p>

                  <p
                    style={{
                      color: "#4B4B4B",
                      marginBottom: "12px",
                      fontSize: "10px",
                    }}
                  >
                    to {shipment?.tp_primary_contact?.email}
                  </p>
                </>
              )}
              {isEditing ? (
                <div
                  ref={editableRef}
                  contentEditable
                  suppressContentEditableWarning
                  style={{
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "20px",
                    whiteSpace: "pre-wrap",
                    outline: "none",
                    minHeight: "120px",
                    padding: "8px",
                    borderRadius: "6px",
                    background: "#F6F6F6",
                  }}
                >
                  {message}
                </div>
              ) : (
                <p
                  style={{
                    marginBottom: "0",
                    whiteSpace: "pre-wrap",
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "20px",
                  }}
                >
                  {message}
                </p>
              )}
            </div>
          </Flex>
        </div>
        <p
          style={{
            fontSize: "12px",
            lineHeight: "20px",
            color: "#5E5E5E",
            marginBottom: "24px",
          }}
        >
          <span style={{ fontWeight: 600 }}>Note:</span> Once the initial email
          is sent, the {decodedUserToken()?.company_name} AI assistant will handle the communication with
          the recipient thereafter. You won't be able to see the email thread or
          intervene until after the exchange is marked completed by the AI
          assistant.
        </p>

        <Flex justify="end" gap="16px">
          <CustomButton
            text="Cancel"
            onClick={onClose}
            bgColor="transparent"
            color="black"
            fontSize="14px"
            fontWeight="600"
            boxShadow="none"
            height="48px"
          />
          <CustomButton
            text="Start AI email thread"
            onClick={() => onConfirm(message, subject)}
            bgColor="black"
            color="white"
            fontSize="14px"
            fontWeight="600"
            height="48px"
            borderRadius="8px"
          />
        </Flex>
      </Modal>
    </>
  );
};

export default EmailTranscriptModal;
